import React, { useState } from "react";
import {
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  FileOutlined,
  PlusOutlined,
  PushpinOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Divider,
  Empty,
  message,
  Popconfirm,
  Spin,
  Switch,
  Tag,
  Timeline,
  Typography,
  Upload,
} from "antd";
import dayjs from "dayjs";
import orderBy from "lodash.orderby";
import { useTranslation } from "react-i18next";

import { PermissionKey, PermissionScope, UserRole } from "@fitness-app/data-models";
import { AuthorTypeEnum } from "@fitness-app/data-models/entities/AuthorTypeEnum";
import {
  ClientNoteCategory,
  ClientNoteType,
  type ClientNote,
  type ClientNoteWithAuthorData,
} from "@fitness-app/data-models/entities/ClientNote";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";

import ModalForm from "~/components/ModalForm/ModalForm";
import { usePermission } from "~/hooks/usePermission";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { type ClientNoteFormModel } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeNotes/types";
import { useTraineeId } from "~/modules/Trainee/TraineeProfile/scope";
import { ClientNoteContent } from "./ClientNoteContent";
import ClientNoteForm from "./ClientNoteForm";
import { NoteAuthorAvatar, NoteAuthorTypeTag } from "./NoteAuthor";
import { useAddTraineeNote, useEditTraineeNote, useRemoveTraineeNote, useTraineeNotes } from "./useTraineeNotes";

export const mapCategoryColor = {
  [ClientNoteCategory.General]: undefined,
  [ClientNoteCategory.Training]: "blue",
  [ClientNoteCategory.Nutrition]: "gold",
  [ClientNoteCategory.Health]: "red",
  [ClientNoteCategory.Management]: "green",
};

const TraineeNotes = () => {
  const { t } = useTranslation("trainees");
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [noteModel, setModel] = useState<ClientNote | null>(null);
  const traineeId = useTraineeId();
  const { data = [], isLoading } = useTraineeNotes(traineeId);
  const addNoteMutation = useAddTraineeNote();
  const editNoteMutation = useEditTraineeNote();
  const removeNoteMutation = useRemoveTraineeNote();
  const { role, userId, isSalesRep } = useUserRole();
  // Cudzą notatkę kasuje ten, kto prowadzi klientki całego konta.
  const canModerateNotes = usePermission(PermissionKey.CLIENTS_WRITE, PermissionScope.ALL);

  const updateNote = async (note: Partial<ClientNote>, { id, authorId }: ClientNote) => {
    try {
      await editNoteMutation.mutateAsync({ note, traineeId, noteId: id, authorId });
      return true;
    } catch {
      void message.error("Nie udało się zapisać notatki. Spróbuj ponownie.");
      return false;
    }
  };

  const handleSubmit = async (formData: ClientNoteFormModel) => {
    if (noteModel) {
      if (await updateNote(formData, noteModel)) {
        setIsFormVisible(false);
        setModel(null);
      }
    } else {
      const model: ClientNote = {
        attachments: formData.attachments || [],
        id: generateUniqId(),
        traineeId,
        content: formData.content,
        type: formData.type || ClientNoteType.Text,
        category: formData.category || ClientNoteCategory.General,
        showInDashboard: formData.showInDashboard ?? false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        pinned: false,
        authorType: isSalesRep
          ? AuthorTypeEnum.Sales
          : role === UserRole.TRAINER
            ? AuthorTypeEnum.Admin
            : AuthorTypeEnum.Trainer,
        authorId: userId,
      };

      try {
        await addNoteMutation.mutateAsync(model);
        setIsFormVisible(false);
        setModel(null);
      } catch {
        void message.error("Nie udało się dodać notatki. Spróbuj ponownie.");
      }
    }
  };

  const editNote = (note: ClientNote) => {
    setModel(note);
    setIsFormVisible(true);
  };

  const toggleIsPinnedNote = async (note: ClientNote) => {
    await updateNote({ pinned: !note.pinned }, note);
  };

  const onVisibilityChange = async (showInDashboard: boolean, note: ClientNote) => {
    await updateNote({ showInDashboard }, note);
  };

  const removeNote = (id: string) => {
    removeNoteMutation.mutate({ noteId: id, traineeId });
  };

  const createTimelineItem = (item: ClientNoteWithAuthorData) => {
    if (item.type === ClientNoteType.Event) {
      return (
        <Timeline.Item
          key={item.id}
          position="left"
          dot={<ThunderboltOutlined style={{ color: "black", fontSize: "16px" }} />}
        >
          <Card title={t("students:timeline.items.lifeEvent")} size="small">
            <p>{item.type}</p>
            <p className="whitespace-pre-wrap">{item.content}</p>
            <Typography.Text type="secondary">
              {`${t("students:timeline.date")}: ${dayjs(item.createdAt).format("DD.MM.YYYY HH:mm")}`}
            </Typography.Text>
          </Card>
        </Timeline.Item>
      );
    }

    return (
      <Timeline.Item key={item.id} position="left" dot={<FileOutlined style={{ color: "black", fontSize: "16px" }} />}>
        <Card
          title={
            <div className="flex flex-col gap-y-2 py-1">
              <div className="flex gap-x-2">
                <NoteAuthorAvatar note={item} />
                <Typography.Text>Notatka tekstowa</Typography.Text>
              </div>

              <div className="flex items-baseline gap-x-2">
                <Typography.Text type="secondary" className="text-sm">
                  <ClockCircleOutlined style={{ marginRight: 8, marginBottom: 10 }} />
                  {item.createdAt ? dayjs(item.createdAt).format("DD.MM.YYYY HH:mm") : ""}
                </Typography.Text>

                <Tag color={mapCategoryColor[item.category]}>
                  {t(`notes.categories.${item.category || ClientNoteCategory.General}`)}
                </Tag>
                <NoteAuthorTypeTag note={item} />
              </div>
            </div>
          }
          className={item.pinned ? "border border-yellow-500" : ""}
          extra={
            <div className="flex items-center gap-2">
              {/* Pin + dashboard visibility mutate the note; sales reps may only
                  touch their own (RLS enforces it), so hide these for others. */}
              {(!isSalesRep || item.authorId === userId) && (
                <>
                  <Switch
                    value={item.showInDashboard}
                    onChange={(checked) => onVisibilityChange(checked, item)}
                    checkedChildren={<EyeOutlined />}
                    unCheckedChildren={<EyeInvisibleOutlined />}
                  />
                  <Divider type="vertical" />

                  <Button onClick={() => toggleIsPinnedNote(item)}>
                    <PushpinOutlined />
                  </Button>
                </>
              )}

              {item.authorId === userId && (
                <>
                  <Divider type="vertical" />

                  <Button onClick={() => editNote(item)}>
                    <EditOutlined />
                  </Button>
                </>
              )}

              {item.authorId === userId || canModerateNotes ? (
                <>
                  <Divider type="vertical" />

                  <Popconfirm
                    title={t("notes.deleteTitle")}
                    okText={t("common:yes")}
                    cancelText={t("common:no")}
                    placement="left"
                    onConfirm={() => removeNote(item.id)}
                  >
                    <Button danger>
                      <DeleteOutlined />
                    </Button>
                  </Popconfirm>
                </>
              ) : null}
            </div>
          }
        >
          <ClientNoteContent note={item} />

          {item.attachments?.length ? <Upload fileList={item.attachments} disabled /> : null}
        </Card>
      </Timeline.Item>
    );
  };

  return (
    <Card
      bordered={false}
      className="min-h-full !shadow-none"
      loading={isLoading}
      extra={
        data.length ? (
          <Button size="small" icon={<PlusOutlined />} type="primary" onClick={() => setIsFormVisible(true)}>
            Dodaj notatkę
          </Button>
        ) : null
      }
    >
      {!data?.length ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ marginTop: 80 }}
          description={
            <div>
              <p style={{ marginBottom: 20 }}>Brak zapisanych notatek</p>
              <Button size="small" icon={<PlusOutlined />} type="primary" onClick={() => setIsFormVisible(true)}>
                Dodaj pierwszą notatkę
              </Button>
            </div>
          }
        />
      ) : (
        <Timeline mode="left">{orderBy(data, ["pinned"], ["desc"]).map(createTimelineItem)}</Timeline>
      )}
      <ModalForm
        open={isFormVisible}
        loading={addNoteMutation.isLoading || editNoteMutation.isLoading}
        onCancel={() => {
          setIsFormVisible(false);
          setModel(null);
        }}
        title={noteModel ? t("notes.editFormTitle") : t("notes.formTitle")}
      >
        {isFormVisible ? <ClientNoteForm onSubmit={handleSubmit} model={noteModel} traineeId={traineeId} /> : <Spin />}
      </ModalForm>
    </Card>
  );
};

export default TraineeNotes;
